import { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { Briefcase, ChevronDown, User as UserIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { typeBadge, type OrgFlowNode } from './types';

/** Two-letter initials derived from a node's display name. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0] ?? '';
  if (!first) return '?';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? '';
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase();
}

function OrgNodeCardComponent({ data }: NodeProps<OrgFlowNode>) {
  const { node, hasChildren, collapsed, childCount, isSelected, isMatch, searchActive } = data;

  return (
    <div
      onClick={() => data.onSelect(node.id)}
      className={cn(
        'group relative w-60 cursor-pointer rounded-xl border border-border/60 bg-card/80 px-3 py-2.5 shadow-card backdrop-blur-sm transition',
        'hover:border-border hover:shadow-card-hover',
        isSelected && 'ring-2 ring-primary',
        isMatch && !isSelected && 'ring-2 ring-primary/70',
        searchActive && !isMatch && 'opacity-40',
      )}
    >
      <Handle type="target" position={Position.Top} isConnectable={false} className="!h-1.5 !w-1.5 !border-0 !bg-border" />

      <div className="flex items-center gap-2.5">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
          {initials(node.name)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">{node.name}</p>
          <span className={cn('mt-0.5', typeBadge)}>{node.level_type_name}</span>
        </div>
      </div>

      {(node.designation_count > 0 || node.employee_count > 0) && (
        <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
          {node.designation_count > 0 && (
            <span className="inline-flex items-center gap-1">
              <Briefcase className="h-3 w-3" />
              {node.designation_count}
            </span>
          )}
          {node.employee_count > 0 && (
            <span className="inline-flex items-center gap-1">
              <UserIcon className="h-3 w-3" />
              {node.employee_count}
            </span>
          )}
        </div>
      )}

      <Handle type="source" position={Position.Bottom} isConnectable={false} className="!h-1.5 !w-1.5 !border-0 !bg-border" />

      {hasChildren && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            data.onToggleCollapse(node.id);
          }}
          title={collapsed ? `Expand ${childCount} child node(s)` : 'Collapse'}
          className="absolute -bottom-3 left-1/2 z-10 inline-flex -translate-x-1/2 items-center gap-1 rounded-full border border-border/60 bg-card px-1.5 py-0.5 text-muted-foreground shadow-sm transition hover:text-foreground"
        >
          <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', collapsed && '-rotate-90')} />
          {collapsed && <span className="text-[10px] font-medium leading-none">{childCount}</span>}
        </button>
      )}
    </div>
  );
}

export const OrgNodeCard = memo(OrgNodeCardComponent);
